import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Language } from '../contexts/TranslationContext';
import { claimGuestAnswer, type GuestAnswerReceipt } from '../services/publicGuestQuestion';
import { ApiError } from '../types/errors';
import { isBotProtectionError } from '../utils/botProtectionErrors';
import { isDisposableEmailError } from '../utils/disposableEmailErrors';
import { useBotProtection } from './useBotProtection';

type ClaimError = 'disposable' | 'protection' | 'expired' | 'rateLimit' | 'request';

export function useGuestAnswerClaim(receipt: GuestAnswerReceipt | null, locale: Language) {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState<ClaimError | null>(null);
  const sending = useRef(false);
  const bot = useBotProtection('registration', { size: 'compact', requireSiteKey: true });
  const mutation = useMutation({
    retry: false,
    mutationFn: async () => {
      if (!receipt || Date.parse(receipt.expiresAt) <= Date.now()) throw new Error('GUEST_ANSWER_EXPIRED');
      const captchaToken = await bot.getCaptchaToken();
      if (!captchaToken) throw new Error('BOT_PROTECTION_UNAVAILABLE');
      const normalized = email.trim().toLowerCase();
      await claimGuestAnswer({ receiptToken: receipt.token, email: normalized, locale, captchaToken, website });
      return normalized;
    },
  });
  const submit = async () => {
    if (sending.current || !email.trim() || sentTo) return;
    sending.current = true;
    setError(null);
    try { setSentTo(await mutation.mutateAsync()); }
    catch (failure) {
      setError(isDisposableEmailError(failure) ? 'disposable' : isBotProtectionError(failure) ? 'protection'
        : failure instanceof Error && failure.message === 'GUEST_ANSWER_EXPIRED' ? 'expired'
          : failure instanceof ApiError && failure.statusCode === 429 ? 'rateLimit'
            : failure instanceof ApiError && [404, 410].includes(failure.statusCode) ? 'expired' : 'request');
    } finally { sending.current = false; bot.resetCaptcha(); }
  };
  return { email, setEmail, website, setWebsite, sentTo, error, submit, isPending: mutation.isPending, isSubmitting: () => sending.current, bot };
}
